import {
  AssetTypeSchema,
  type AssetTypeType,
  type TargetTypeType,
} from '@terra/shared-schemas-orchestrator';

const AD_GRAPH_NODE_ID_KIND: Record<string, string> = {
  DOMAIN_CONTROLLER: 'COMPUTER',
};

const makeInternalNetworkAdEntityNodeId = ({
  entityKind,
  objectId,
}: {
  entityKind: string;
  objectId: string;
}) =>
  `${AD_GRAPH_NODE_ID_KIND[entityKind] ?? entityKind}:${objectId.trim().toLowerCase()}`;

export const NAVIGABLE_DISCOVERED_ASSET_KINDS = [
  'NETBLOCK',
  'DOMAIN',
  'HOST',
  'GATEWAY',
  'NETWORK_SERVICE',
  'SOFTWARE',
  'STORAGE_SERVICE',
] as const;

export const NAVIGABLE_INTERNAL_NETWORK_DISCOVERED_ASSET_KINDS = [
  'USER',
  'GROUP',
  'COMPUTER',
  'OU',
  'DOMAIN',
  'GPO',
  'DOMAIN_CONTROLLER',
  'CERT_TEMPLATE',
  'CONTAINER',
] as const;

export type NavigableDiscoveredAssetKind =
  (typeof NAVIGABLE_DISCOVERED_ASSET_KINDS)[number];

export type NavigableInternalNetworkDiscoveredAssetKind =
  (typeof NAVIGABLE_INTERNAL_NETWORK_DISCOVERED_ASSET_KINDS)[number];

const NAVIGABLE_DISCOVERED_ASSET_KIND_SET: ReadonlySet<string> = new Set(
  NAVIGABLE_DISCOVERED_ASSET_KINDS,
);

const NAVIGABLE_INTERNAL_NETWORK_DISCOVERED_ASSET_KIND_SET: ReadonlySet<string> =
  new Set(NAVIGABLE_INTERNAL_NETWORK_DISCOVERED_ASSET_KINDS);

export function isNavigableDiscoveredAssetKind(
  kind: string | null | undefined,
): kind is NavigableDiscoveredAssetKind {
  return kind != null && NAVIGABLE_DISCOVERED_ASSET_KIND_SET.has(kind);
}

export function isNavigableInternalNetworkDiscoveredAssetKind(
  kind: string | null | undefined,
): kind is NavigableInternalNetworkDiscoveredAssetKind {
  return (
    kind != null &&
    NAVIGABLE_INTERNAL_NETWORK_DISCOVERED_ASSET_KIND_SET.has(kind)
  );
}

export function canNavigateToDiscoveredAsset({
  assetType,
  discoveredAssetType,
  targetType,
}: {
  assetType: AssetTypeType;
  discoveredAssetType?: string | null;
  targetType?: TargetTypeType | null;
}): boolean {
  if (assetType !== AssetTypeSchema.enum.DISCOVERED_ASSET) {
    return false;
  }

  if (targetType === 'EXTERNAL_NETWORK') {
    return isNavigableDiscoveredAssetKind(discoveredAssetType);
  }

  if (targetType === 'INTERNAL_NETWORK') {
    return isNavigableInternalNetworkDiscoveredAssetKind(discoveredAssetType);
  }

  return false;
}

export type DiscoveredAssetExternalNetworkNavigation = {
  surfaceId: string;
  entityKind: NavigableDiscoveredAssetKind;
  entityId: string;
};

export type DiscoveredAssetInternalNetworkNavigation = {
  surfaceId: string;
  adEntityNodeId: string;
};

export function resolveDiscoveredAssetExternalNetworkNavigation({
  assetId,
  assetType,
  discoveredAssetType,
  targetType,
  surfaceId,
}: {
  assetId: string;
  assetType: AssetTypeType;
  discoveredAssetType?: string | null;
  targetType?: TargetTypeType | null;
  surfaceId?: string | null;
}): DiscoveredAssetExternalNetworkNavigation | undefined {
  if (
    !surfaceId ||
    targetType !== 'EXTERNAL_NETWORK' ||
    !isNavigableDiscoveredAssetKind(discoveredAssetType) ||
    !canNavigateToDiscoveredAsset({
      assetType,
      discoveredAssetType,
      targetType,
    })
  ) {
    return;
  }

  return {
    surfaceId,
    entityKind: discoveredAssetType,
    entityId: assetId,
  };
}

export function resolveDiscoveredAssetInternalNetworkNavigation({
  assetId,
  assetType,
  discoveredAssetType,
  targetType,
  surfaceId,
}: {
  assetId: string;
  assetType: AssetTypeType;
  discoveredAssetType?: string | null;
  targetType?: TargetTypeType | null;
  surfaceId?: string | null;
}): DiscoveredAssetInternalNetworkNavigation | undefined {
  if (
    !surfaceId ||
    targetType !== 'INTERNAL_NETWORK' ||
    !isNavigableInternalNetworkDiscoveredAssetKind(discoveredAssetType) ||
    !canNavigateToDiscoveredAsset({
      assetType,
      discoveredAssetType,
      targetType,
    })
  ) {
    return;
  }

  return {
    surfaceId,
    adEntityNodeId: makeInternalNetworkAdEntityNodeId({
      entityKind: discoveredAssetType,
      objectId: assetId,
    }),
  };
}
